import { ModulesLoader } from '@wix/ssr-modules-loader'
import {
	registry,
	ILibraryTopology,
	ISiteAssetsComponentsRegistry,
	ISiteAssetsModel,
} from '@wix/editor-elements-registry/2.0/siteAssets'
import { createRegistryInstanceCache } from '@wix/editor-elements-registry/2.0/createRegistryInstanceCache'
import { IRegistryRunAndReport, getReportMetricName } from './metrics'

export { ILibraryTopology }
export type IComponentMappers = Record<string, ISiteAssetsModel['mapper']>

export interface ICreateRegistrySiteAssetsParams {
	libraries: Array<ILibraryTopology>
	modulesLoader: ModulesLoader
}
export interface IComponentsRegistrySiteAssets {
	getRegistryAPI: () => ISiteAssetsComponentsRegistry
	loadComponentMappers: () => Promise<IComponentMappers>
}

const METRIC_CREATE_REGISTRY = getReportMetricName({ host: 'siteAssets' })
const METRIC_CREATE_REGISTRY_CACHED = getReportMetricName({ host: 'siteAssets', cached: true })

const cache = createRegistryInstanceCache<ISiteAssetsComponentsRegistry>()

async function getRegistryAPI({
	libraries,
	modulesLoader,
	runAndReport,
}: ICreateRegistrySiteAssetsParams & IRegistryRunAndReport): Promise<ISiteAssetsComponentsRegistry> {
	return cache.getRegistryAPI({
		libraries,
		factory: () => {
			return runAndReport(METRIC_CREATE_REGISTRY, () =>
				registry({
					fetcher: (url) => {
						const content = (modulesLoader as ModulesLoader).absoluteUrl<string>(url, {
							shouldUseNodeRequire: false,
						})

						return content
					},
					mode: 'eager',
					libraries,
				})
			)
		},
	})
}

export async function createComponentsRegistrySiteAssets(options: {
	libraries: Array<ILibraryTopology>
	modulesLoader: ModulesLoader
	runAndReport: <T>(metric: string, fn: () => Promise<T>) => Promise<T>
}): Promise<IComponentsRegistrySiteAssets> {
	const { runAndReport } = options

	const registryAPI = await runAndReport(METRIC_CREATE_REGISTRY_CACHED, () => getRegistryAPI(options))

	return {
		async loadComponentMappers() {
			const models = await registryAPI.loadAllComponents()

			return Object.keys(models)
				.filter((componentType) => models[componentType].mapper)
				.reduce<IComponentMappers>((acc, componentType) => {
					return {
						...acc,
						[componentType]: models[componentType].mapper,
					}
				}, {})
		},

		getRegistryAPI() {
			return registryAPI
		},
	}
}
